/**
 * Helper functions for asset URL handling
 *
 * Handles presigned URLs, thumbnail selection, and fallbacks.
 */

import type { AssetRendition, AssetVariant } from '@/types/channel-packages';

/**
 * Get the best available thumbnail URL for an asset rendition.
 *
 * Priority:
 * 1. THUMB variant (optimized thumbnail)
 * 2. USER_PREVIEW variant (larger preview)
 * 3. s3_key_original (fallback to original)
 * 4. Placeholder image
 *
 * @param rendition Asset rendition object
 * @param preferredPurpose Preferred variant purpose (default: 'THUMB')
 * @returns URL string (presigned or placeholder)
 */
export function getAssetThumbnailUrl(
  rendition: AssetRendition,
  preferredPurpose: AssetVariant['purpose'] = 'THUMB'
): string {
  // 1. Try preferred purpose
  const preferred = rendition.variants?.find((v) => v.purpose === preferredPurpose);
  if (preferred?.s3_key) {
    return getPresignedUrl(preferred.s3_key);
  }

  // 2. Try THUMB as fallback
  if (preferredPurpose !== 'THUMB') {
    const thumb = rendition.variants?.find((v) => v.purpose === 'THUMB');
    if (thumb?.s3_key) {
      return getPresignedUrl(thumb.s3_key);
    }
  }

  // 3. Try any ready variant
  const anyReady = rendition.variants?.find((v) => v.s3_key && v.status === 'ready');
  if (anyReady?.s3_key) {
    return getPresignedUrl(anyReady.s3_key);
  }

  // 4. Fallback to original
  if (rendition.s3_key_original) {
    return getPresignedUrl(rendition.s3_key_original);
  }

  // 5. Fallback to legacy rendition_url
  if (rendition.rendition_url) {
    return rendition.rendition_url;
  }

  // 6. Placeholder
  return '/images/placeholder-image.png';
}

/**
 * Get secure proxy URL for asset by ID (NEW PATTERN).
 *
 * Replaces presigned URLs with backend proxy routes.
 *
 * @param assetId Asset UUID
 * @param variant 'file' (original), 'thumb', 'preview', 'download'
 * @returns Proxy URL like /api/v1/asset/{id}/file
 */
export function getAssetUrl(
  assetId: string,
  variant: 'file' | 'thumb' | 'preview' | 'download' = 'file'
): string {
  return `/api/v1/asset/${assetId}/${variant}`;
}

/**
 * @deprecated Use getAssetUrl(assetId) instead.
 *
 * Get presigned URL for an S3 key.
 *
 * Uses the backend presign endpoint which redirects to the actual S3 presigned URL.
 *
 * @param s3Key S3 key
 * @returns Presigned URL via backend proxy
 */
export function getPresignedUrl(s3Key: string): string {
  console.warn('[DEPRECATED] getPresignedUrl() is deprecated. Use getAssetUrl(assetId) instead.');

  // Already a full URL - return as-is
  if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
    return s3Key;
  }

  // Use the backend presign endpoint (redirects to presigned S3 URL)
  return `/api/v1/assets/presign?key=${encodeURIComponent(s3Key)}`;
}

/**
 * Get the best preview URL for an asset (larger size).
 *
 * @param rendition Asset rendition object
 * @returns URL string for preview
 */
export function getAssetPreviewUrl(rendition: AssetRendition): string {
  return getAssetThumbnailUrl(rendition, 'USER_PREVIEW');
}

/**
 * Get original asset URL.
 *
 * @param rendition Asset rendition object
 * @returns Original URL or placeholder
 */
export function getAssetOriginalUrl(rendition: AssetRendition): string {
  if (rendition.s3_key_original) {
    return getPresignedUrl(rendition.s3_key_original);
  }
  return '/images/placeholder-image.png';
}

/**
 * Format file size in human-readable format.
 *
 * @param bytes File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get image dimensions as string.
 *
 * @param rendition Asset rendition object
 * @returns Dimensions string (e.g., "1920 × 1080") or null
 */
export function getImageDimensions(rendition: AssetRendition): string | null {
  if (rendition.width && rendition.height) {
    return `${rendition.width} × ${rendition.height}`;
  }
  return null;
}
